import { useAnalytics } from '@contexts/AnalyticsContext'
import { PlatformSlug } from '@models/Platform'
import { ProductType } from '@models/Product'
import { UserAnalyticsGraphApplicationRegistration } from '@models/user/UserAnalyticsGraph'
import { useTenant } from '@modules/tenant/contexts/tenant/TenantContext'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import {
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Row,
  Space,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import CountCard from './CountCard'

const { Text } = Typography

// Analytics-scoped display override: the shared whatsapp platform is surfaced as
// "Chatbot" in the community analytics surfaces only. We intentionally do NOT
// rename `platforms.whatsapp.name` globally (it's reused by device/venue/connector
// UIs), so this label is applied locally at render time.
const platformDisplayName = (slug: PlatformSlug, fallback: string): string =>
  slug === 'whatsapp' ? 'Chatbot' : fallback

type Props = {
  //
}

const RegistrationOverviewChart = ({}: Props) => {
  const { allPlatforms, platforms } = useTenant()
  const { selectedProduct, selectedDates } = useAnalytics()
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [counts, setCounts] = useState<{
    total: number
    ios: number
    android: number
    whatsapp: number
  }>()
  const [chartData, setChartData] =
    useState<UserAnalyticsGraphApplicationRegistration['registrations']>()
  const [displayCount] = useState<boolean>(false)
  const [displayDates] = useState<boolean>(true)

  interface CustomizedAxisTickProps {
    x: number
    y: number
    stroke?: string
    payload: {
      value: string
    }
  }
  const CustomizedAxisTick: React.FC<CustomizedAxisTickProps> = ({
    x,
    y,
    payload,
  }) => {
    return displayDates ? (
      <g transform={`translate(${x},${y})`}>
        <text x={20} y={0} dy={16} textAnchor="end" fill="#bbb">
          {dayjs(payload.value).format('DD MMM')}
        </text>
      </g>
    ) : null
  }

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active: boolean | undefined
    payload: any[] | undefined
    label: string
  }) => {
    if (active && payload && payload.length) {
      return (
        <Card
          styles={{
            body: { padding: 10 },
          }}
        >
          <Space className="w-full space-between">
            <Text>{dayjs(label).format('DD MMMM YYYY')}</Text>
            <Text type="secondary">
              Total : {payload.reduce((sum, item) => sum + item.value, 0)}
            </Text>
          </Space>

          <Divider style={{ marginTop: 10, marginBottom: 10 }} />
          <Descriptions
            column={1}
            layout="horizontal"
            size="small"
            style={{ width: 250 }}
          >
            {payload.map((item: any) =>
              allPlatforms[item.name as PlatformSlug] &&
              platforms[item.name as PlatformSlug]?.identifier != undefined ? ( //oncheck si le name de la data est bien une platform
                <Descriptions.Item
                  label={
                    <Space size={5}>
                      {allPlatforms[item.name as PlatformSlug].icon}
                      <Text>
                        {platformDisplayName(
                          item.name as PlatformSlug,
                          allPlatforms[item.name as PlatformSlug].name
                        )}
                      </Text>
                    </Space>
                  }
                >
                  {`${item.value} registration${item.value > 1 ? 's' : ''}`}
                </Descriptions.Item>
              ) : null
            )}
          </Descriptions>
        </Card>
      )
    }
    return null
  }

  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, height, value } = props
    return value > 0 && displayCount ? (
      <g>
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {value}
        </text>
      </g>
    ) : null
  }

  useEffect(() => {
    if (!selectedDates) return

    setIsLoading(true)

    fetchAnalyticsGraph<UserAnalyticsGraphApplicationRegistration>({
      entity: 'user',
      kind: 'platforms-registration',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      product: selectedProduct,
    })
      .then((data) => {
        setChartData(data.data.registrations)
        setCounts({
          total: data.data.usersCount,
          ...data.data.registrations.reduce(
            (acc, item) => ({
              ios: acc.ios + item.ios,
              android: acc.android + item.android,
              whatsapp: acc.whatsapp + item.whatsapp,
            }),
            { ios: 0, android: 0, whatsapp: 0 }
          ),
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [selectedDates])

  return (
    <Row gutter={[16, 16]}>
      <Col span={6}>
        <CountCard
          title={'Total'}
          loading={isLoading}
          currentValue={counts?.total}
        />
      </Col>
      <Col span={6}>
        <CountCard
          loading={isLoading}
          title={'Total for period'}
          platform={allPlatforms.ios}
          currentValue={counts?.ios}
          disabled={platforms.ios === undefined}
        />
      </Col>
      <Col span={6}>
        <CountCard
          loading={isLoading}
          title={'Total for period'}
          platform={allPlatforms.android}
          currentValue={counts?.android}
          disabled={platforms.android === undefined}
        />
      </Col>
      <Col span={6}>
        <CountCard
          loading={isLoading}
          title={'Total for period'}
          // Analytics-scoped label override (see platformDisplayName): show the
          // whatsapp platform as "Chatbot" without mutating the shared platform.
          platform={{
            ...allPlatforms.whatsapp,
            name: platformDisplayName('whatsapp', allPlatforms.whatsapp.name),
          }}
          currentValue={counts?.whatsapp}
          disabled={platforms.whatsapp === undefined}
        />
      </Col>
      <Col span={24}>
        <Card loading={isLoading} styles={{ header: { padding: 10 } }}>
          {chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={
                  selectedProduct === ProductType.All
                    ? chartData
                    : chartData.map((chartDataElement) => ({
                        date: chartDataElement.date,
                        ios:
                          selectedProduct === ProductType.App
                            ? chartDataElement.ios
                            : undefined,
                        android:
                          selectedProduct === ProductType.App
                            ? chartDataElement.android
                            : undefined,
                        whatsapp:
                          selectedProduct === ProductType.Chatbot
                            ? chartDataElement.whatsapp
                            : undefined,
                      }))
                }
              >
                <YAxis tickLine={false} stroke={'#bbb'} width={30} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  stroke={'#bbb'}
                  tick={(props) => <CustomizedAxisTick {...props} />}
                />
                <Tooltip
                  content={(content) => (
                    <CustomTooltip
                      active={content.active}
                      payload={content.payload as any[]}
                      label={String(content.label ?? '')}
                    />
                  )}
                />
                <Bar dataKey="ios" stackId="a" fill={allPlatforms.ios.color[3]}>
                  <LabelList
                    dataKey="ios"
                    position="inside"
                    content={renderCustomizedLabel}
                  />
                </Bar>
                <Bar
                  dataKey="android"
                  stackId="a"
                  fill={allPlatforms.android.color[3]}
                >
                  <LabelList
                    dataKey="android"
                    position="inside"
                    content={renderCustomizedLabel}
                  />
                </Bar>
                <Bar
                  dataKey="whatsapp"
                  stackId="a"
                  fill={allPlatforms.whatsapp.color[3]}
                >
                  <LabelList
                    dataKey="whatsapp"
                    position="inside"
                    content={renderCustomizedLabel}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'No data to display'}
            />
          )}
        </Card>
      </Col>
    </Row>
  )
}

type RegistrationOverviewChartType = { Chart: typeof RegistrationOverviewChart }

export { RegistrationOverviewChart, type RegistrationOverviewChartType }
